// @tennis-matches/services/location
// User-Standort & Zeitzone – Persistenz im bestehenden user_settings JSONB.

import { loadUserSettings, saveUserSettings } from "../settings/index.js";

const FALLBACK_TIMEZONE = "Europe/Berlin";

/**
 * @typedef {Object} UserLocation
 * @property {string} timezone
 * @property {number|null} latitude
 * @property {number|null} longitude
 * @property {string} label
 * @property {string} city
 * @property {string} source - 'user' | 'club_location' | 'fallback'
 * @property {string} updatedAt
 */

function asNum(x) {
  if (x === null || x === undefined || x === "") return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function asStr(x) {
  return String(x ?? "").trim();
}

/** @returns {UserLocation} */
export function defaultUserLocation() {
  return {
    timezone: FALLBACK_TIMEZONE,
    latitude: null,
    longitude: null,
    label: "",
    city: "",
    source: "fallback",
    updatedAt: "",
  };
}

/** @returns {Promise<{ok:boolean, location: UserLocation, settings: object}>} */
export async function loadUserLocation() {
  const res = await loadUserSettings();
  const settings = (res?.settings && typeof res.settings === "object") ? res.settings : {};
  const raw = settings.location && typeof settings.location === "object" ? settings.location : null;

  const location = raw
    ? {
        timezone: asStr(raw.timezone) || FALLBACK_TIMEZONE,
        latitude: asNum(raw.latitude),
        longitude: asNum(raw.longitude),
        label: asStr(raw.label),
        city: asStr(raw.city),
        source: asStr(raw.source) || "user",
        updatedAt: asStr(raw.updatedAt),
      }
    : defaultUserLocation();

  return { ok: true, location, settings };
}

/**
 * @param {Partial<UserLocation>} patch
 * @returns {Promise<{ok:boolean, persisted?:boolean, reason?:string, location?: UserLocation}>}
 */
export async function saveUserLocation(patch) {
  if (!patch || typeof patch !== "object") {
    return { ok: false, reason: "invalid-input" };
  }

  const current = await loadUserSettings();
  const settings = (current?.settings && typeof current.settings === "object") ? current.settings : {};
  const previous = settings.location && typeof settings.location === "object" ? settings.location : {};

  /** @type {UserLocation} */
  const next = {
    timezone: asStr(patch.timezone ?? previous.timezone) || FALLBACK_TIMEZONE,
    latitude: asNum(patch.latitude ?? previous.latitude),
    longitude: asNum(patch.longitude ?? previous.longitude),
    label: asStr(patch.label ?? previous.label),
    city: asStr(patch.city ?? previous.city),
    source: asStr(patch.source ?? previous.source) || "user",
    updatedAt: new Date().toISOString(),
  };

  const merged = { ...settings, location: next };
  const result = await saveUserSettings(merged);
  if (!result?.ok) return { ok: false, reason: result?.reason || "save-failed" };
  return {
    ok: true,
    persisted: !!result.persisted,
    reason: result.reason,
    location: next,
  };
}

export const LOCATION_FALLBACK_TIMEZONE = FALLBACK_TIMEZONE;
